import { useState } from 'react';
import type { TileId } from '@shared/constants.ts';
import { Icon } from '../Icon.tsx';

export type Tile = {
  id: TileId;
  label: string;
  accent: string;
  icon: (p: { s: number; strokeWidth: number }) => React.ReactNode;
  hint?: string;
};

export const DEFAULT_TILES: Tile[] = [
  { id: 'sleep', label: 'Sleep', accent: 'sleep-night', icon: (p) => <Icon.moon {...p} /> },
  { id: 'bottle', label: 'Bottle', accent: 'bottle', icon: (p) => <Icon.bottle {...p} /> },
  { id: 'bL', label: 'Breast L', accent: 'breast', icon: (p) => <Icon.breast {...p} /> },
  { id: 'bR', label: 'Breast R', accent: 'breast', icon: (p) => <Icon.breast {...p} /> },
  { id: 'pump', label: 'Pump', accent: 'pump', icon: (p) => <Icon.pump {...p} /> },
  { id: 'solid', label: 'Solid', accent: 'solid', icon: (p) => <Icon.solid {...p} /> },
  { id: 'diaper', label: 'Diaper', accent: 'poop', icon: (p) => <Icon.diaper {...p} /> },
];

/** Opt-in tiles — appear in the editor's hidden pool until the user adds
 *  them to the quick-log grid. */
export const EXTRA_TILES: Tile[] = [
  { id: 'measurement', label: 'Measurement', accent: 'accent', icon: (p) => <Icon.chart {...p} /> },
  { id: 'milestone', label: 'Milestone', accent: 'breast', icon: (p) => <Icon.sparkle {...p} /> },
  { id: 'medication', label: 'Medication', accent: 'danger', icon: (p) => <Icon.bottle {...p} /> },
  { id: 'temperature', label: 'Temperature', accent: 'danger', icon: (p) => <Icon.heart {...p} /> },
];

export const ALL_TILES: Tile[] = [...DEFAULT_TILES, ...EXTRA_TILES];

type Props = {
  tiles?: Tile[];
  hints?: Partial<Record<TileId, string>>;
  activeIds?: Set<TileId>;
  onPrimary: (id: TileId) => void;
  onMore: (id: TileId) => void;
  density?: '2col' | '3col';
  collapsed: boolean;
  onToggle: () => void;
};

export function ButtonGrid({
  tiles = DEFAULT_TILES,
  hints = {},
  activeIds = new Set<TileId>(),
  onPrimary,
  onMore,
  density = '3col',
  collapsed,
  onToggle,
}: Props) {
  return (
    <div style={{ padding: '0 16px' }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 4px 10px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-3)',
            letterSpacing: 0.04,
            textTransform: 'uppercase',
          }}
        >
          Quick log
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            aria-label={collapsed ? 'Expand quick log' : 'Collapse quick log'}
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform .22s ease',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <Icon.chevD s={14} strokeWidth={2.2} />
          </button>
        </div>
      </div>

      {collapsed ? (
        <div
          className="lt-scroll"
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            padding: '2px 4px 12px',
            margin: '0 -4px',
            maskImage: 'linear-gradient(to right, black calc(100% - 24px), transparent)',
          }}
        >
          {tiles.map((t) => (
            <ActionPill
              key={t.id}
              tile={t}
              active={activeIds.has(t.id)}
              onClick={() => onPrimary(t.id)}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: density === '2col' ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: 10,
            paddingBottom: 4,
          }}
        >
          {tiles.map((t) => (
            <ActionTile
              key={t.id}
              tile={t}
              active={activeIds.has(t.id)}
              hint={hints[t.id]}
              onPrimary={() => onPrimary(t.id)}
              onMore={() => onMore(t.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionTile({
  tile,
  active,
  hint,
  onPrimary,
  onMore,
}: {
  tile: Tile;
  active: boolean;
  hint?: string;
  onPrimary: () => void;
  onMore: () => void;
}) {
  const Icn = tile.icon;
  const tint = `var(--${tile.accent})`;
  const [pressing, setPressing] = useState(false);
  const longPressTimer = useRef<number | null>(null);

  function startPress() {
    setPressing(true);
    longPressTimer.current = window.setTimeout(() => {
      onMore();
      setPressing(false);
      longPressTimer.current = null;
    }, 450);
  }
  function endPress(fire = true) {
    if (longPressTimer.current != null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      if (fire) onPrimary();
    }
    setPressing(false);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onPointerDown={startPress}
      onPointerUp={() => endPress(true)}
      onPointerLeave={() => endPress(false)}
      onPointerCancel={() => endPress(false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onPrimary();
      }}
      style={{
        position: 'relative',
        background: active ? `color-mix(in oklab, ${tint} 12%, var(--bg-card))` : 'var(--bg-card)',
        borderRadius: 22,
        border: active ? `1px solid ${tint}` : '1px solid var(--border)',
        padding: '14px 14px 12px',
        aspectRatio: '1 / 1',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: 'manipulation',
        transform: pressing ? 'scale(0.97)' : 'scale(1)',
        transition: 'transform .12s ease, background .15s ease, border-color .15s ease',
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          background: `color-mix(in oklab, ${tint} 18%, transparent)`,
          color: tint,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icn s={22} strokeWidth={1.8} />
      </div>
      <div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: -0.012,
            color: 'var(--text-1)',
          }}
        >
          {tile.label}
        </div>
        {hint && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{hint}</div>
        )}
      </div>
      {/* chevron removed — tap = primary, long-press (~450ms) = onMore (sheet) */}
    </div>
  );
}

function ActionPill({ tile, active, onClick }: { tile: Tile; active: boolean; onClick: () => void }) {
  const Icn = tile.icon;
  const tint = `var(--${tile.accent})`;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px 8px 8px',
        background: active ? `color-mix(in oklab, ${tint} 12%, var(--bg-card))` : 'var(--bg-card)',
        border: active ? `1px solid ${tint}` : '1px solid var(--border)',
        borderRadius: 999,
        color: 'var(--text-1)',
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 999,
          background: `color-mix(in oklab, ${tint} 20%, transparent)`,
          color: tint,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icn s={15} strokeWidth={1.9} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 500, letterSpacing: -0.005 }}>{tile.label}</span>
    </button>
  );
}

import { useRef } from 'react';
